import { createFileRoute, Link } from "@tanstack/react-router";
import { ClipboardCheck, Wifi, Car, MapPin, Phone } from "lucide-react";
import { breadcrumbSchema, canonical, faqPageSchema } from "@/lib/seo";

export const Route = createFileRoute("/blog/what-to-know-before-your-stay")({
  head: () => ({
    meta: [
      { title: "What to Know Before Your Stay - Malak BnB Resort" },
      { name: "description", content: "A first-time guest guide to Malak BnB Resort in Crystal Beach, Ontario: booking, arrival, what's included, and a few local tips before you go." },
      { property: "og:title", content: "What to Know Before Your Stay - Malak BnB Resort" },
      { property: "og:description", content: "Everything a first-time guest should know before staying at Malak BnB Resort in Crystal Beach, Ontario." },
      { property: "og:url", content: "https://malakbnbresort.ca/blog/what-to-know-before-your-stay" },
      { property: "og:image", content: "/images/gallery-bedroom-warm.webp" },
      { "script:ld+json": faqPageSchema(FAQ) },
      {
        "script:ld+json": breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: "What to Know Before Your Stay", path: "/blog/what-to-know-before-your-stay" },
        ]),
      },
    ],
    links: [canonical("/blog/what-to-know-before-your-stay")],
  }),
  component: BeforeYourStayPage,
});

const INCLUDED = [
  "Continental breakfast buffet",
  "Outdoor heated pool",
  "Free Wi-Fi",
  "Air conditioning",
  "Flat screen TV",
  "In-room safe",
];

const FAQ = [
  {
    q: "How do I book a room at Malak BnB Resort?",
    a: "Malak BnB Resort takes direct reservation requests through the booking form. You choose your room and dates, submit the request, and the team personally reviews and confirms your stay before sending payment details. There is no instant booking.",
  },
  {
    q: "What should I know about arrival time?",
    a: "Malak BnB Resort does not use automated check-in. Your estimated arrival time is collected as part of the reservation request, and the team coordinates directly with you if anything needs to change closer to your dates.",
  },
  {
    q: "Is parking available at Malak BnB Resort?",
    a: "Yes. Free parking is available for guests staying at Malak BnB Resort.",
  },
  {
    q: "What should first-time guests know about the area around Malak BnB Resort?",
    a: "Malak BnB Resort is a 5-minute walk from Bay Beach in Crystal Beach, Ontario, with the town centre and several restaurants also within walking distance. Niagara Falls is about a 30-minute drive, and the Peace Bridge into Buffalo, New York is roughly 15 minutes away.",
  },
];

function BeforeYourStayPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[380px] overflow-hidden">
        <img
          src="/images/gallery-bedroom-warm.webp"
          alt="Warmly lit bedroom at Malak BnB Resort in Crystal Beach"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/65 via-foreground/20 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-16 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-3">
              <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
              <span>/</span>
              <span>Before Your Stay</span>
            </div>
            <p className="text-white/70 font-semibold text-sm uppercase tracking-widest mb-3">A Guest Guide</p>
            <h1 className="text-4xl sm:text-5xl font-heading text-white leading-tight max-w-2xl">
              What to Know Before Your Stay at Malak BnB Resort
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg text-muted-foreground space-y-5 leading-relaxed">
            <p>
              First stays at a small, family-run bed and breakfast are a little different from checking into a
              hotel chain, and that is part of the appeal. There is no automated kiosk and no call centre. It is a
              team of real people reviewing your request and coordinating your arrival directly.
            </p>
            <p>
              Here is what to expect, from booking through to your first morning at Malak BnB Resort.
            </p>
          </div>
        </div>
      </section>

      {/* Before you arrive */}
      <section className="pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ClipboardCheck className="h-5 w-5 text-primary" />
                <p className="text-primary font-semibold text-sm uppercase tracking-wider">Before You Arrive</p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-heading text-foreground leading-snug mb-4">
                Booking and Confirmation
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Malak BnB Resort takes direct reservation requests rather than instant online bookings. Submit your
                preferred room and dates, and the team personally reviews and confirms availability before sending
                payment details. It takes a little longer than an automated system, but it means a real person is
                looking at your stay from the start.
              </p>
              <Link to="/reservation-details" className="text-primary font-semibold text-sm hover:underline">
                Read the full booking process →
              </Link>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img src="/images/hero-exterior.webp" alt="Exterior of Malak BnB Resort in Crystal Beach, Ontario" className="w-full object-cover aspect-[4/3]" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Every Room Includes</p>
            <h2 className="text-3xl font-heading text-foreground">What's Already Taken Care Of</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {INCLUDED.map((item) => (
              <div key={item} className="flex items-center gap-3 bg-background rounded-xl p-4 border border-border/50">
                <Wifi className="h-4 w-4 text-primary shrink-0" />
                <span className="text-sm font-medium text-foreground">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Getting around */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-3">
            <Car className="h-5 w-5 text-primary" />
            <p className="text-primary font-semibold text-sm uppercase tracking-wider">Getting Around</p>
          </div>
          <h2 className="text-2xl sm:text-3xl font-heading text-foreground leading-snug mb-4">
            Free Parking and a Walkable Town
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Free parking is available on site, and Crystal Beach itself is small enough to explore on foot. Bay
            Beach, several restaurants, and the town centre are all within walking distance of Malak BnB Resort. A
            car helps if you plan to add a Niagara Falls day trip or explore further into Fort Erie and Ridgeway.
          </p>
        </div>
      </section>

      {/* Local tips */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <MapPin className="h-5 w-5 text-primary" />
            <p className="text-primary font-semibold text-sm uppercase tracking-wider">A Few Local Tips</p>
          </div>
          <h2 className="text-3xl font-heading text-foreground mb-6">Before You Head Out</h2>
          <p className="text-muted-foreground leading-relaxed">
            Summer weekends fill up fast at Bay Beach, so an early morning start makes the most of it before the
            crowds arrive. Niagara Falls is a 30 minute drive and worth arriving by mid-morning to beat the parking
            rush. And if you are hoping for dinner reservations in Crystal Beach or Ridgeway on a Saturday night, it
            is worth calling ahead during peak summer months.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Common Questions</p>
            <h2 className="text-3xl font-heading text-foreground">First-Time Guest Questions</h2>
          </div>
          <div className="space-y-8">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="border-b border-border pb-8 last:border-0 last:pb-0">
                <h3 className="font-heading text-lg text-foreground mb-3">{q}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">Crystal Beach, Ontario</p>
          <h2 className="text-3xl sm:text-4xl font-heading text-primary-foreground leading-tight">
            Ready to plan your first stay?
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Submit a reservation request and the team will personally confirm availability and send payment details directly.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/book" className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg">
              Request Your Stay
            </Link>
            <a href="tel:+12899298055" className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors">
              <Phone className="h-4 w-4" />
              1-289-929-8055
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
